import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import useToast, { type ToastId } from "@/lib/hooks/useToast";

export const UNDO_TOAST_DURATION_MS = 8000;

type UndoToast = {
  message: string;
  description?: ReactNode;
  onUndo: () => void;
};

export function useUndoToast() {
  const { t } = useTranslation();
  const { showToast, dismissToast } = useToast();
  const toastIds = useRef(new Set<ToastId>());

  useEffect(
    () => () => {
      toastIds.current.forEach((toastId) => dismissToast(toastId));
    },
    [dismissToast],
  );

  return useCallback(
    ({ message, description, onUndo }: UndoToast) => {
      const toastId = crypto.randomUUID();
      toastIds.current.add(toastId);
      showToast(message, "info", {
        id: toastId,
        description,
        duration: UNDO_TOAST_DURATION_MS,
        action: {
          label: t("admin.segments.builder.undo"),
          onClick: () => {
            toastIds.current.delete(toastId);
            dismissToast(toastId);
            onUndo();
          },
        },
      });
    },
    [t, showToast, dismissToast],
  );
}
